import { useCallback, useMemo, useState, useRef, type CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ChevronRight, User, LogOut, Keyboard, Sun, Moon, HelpCircle } from 'lucide-react';
import styles from './Header.module.css';
import { useAuthStore } from '../stores/authStore';
import { useThemeToggle } from '../hooks/useMe';
import { useLogout } from '../hooks/useLogin';
import { useClickOutside } from '../hooks/useClickOutside';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useGlobalShortcuts, type ChordDestination } from '../hooks/useGlobalShortcuts';
import { useUnreadCountPoll } from '../hooks/useNotifications';
import { useProjectStore } from '../stores/projectStore';
import { useProjects } from '../hooks/useProjects';
import { chordTarget } from './headerChordTarget';
import { buildCrumbs } from './headerBreadcrumbs';
import { NotificationBell } from './notifications/NotificationBell';
import { GlobalSearchInput } from './search/GlobalSearchInput';
import { ShortcutsModal } from './ShortcutsModal';
import { BrandMark } from './BrandMark';

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  // useThemeToggle wraps useUIStore.setTheme + PATCH /me so the manual
  // toggle persists server-side. Without this, /me refetches would
  // overwrite the local preference with the stale database value.
  const { theme, toggle: toggleTheme } = useThemeToggle();
  const logout = useLogout();
  // WS-4a (C1): 60s poll of the unread count, synced into `uiStore` so
  // the bell badge can read it without a query subscription of its own.
  // SSE remains FAIL for the browser auth model (WS-4b C3.1).
  useUnreadCountPoll();

  // WS-4b (C6) navigation chords. The chord targets are project-scoped
  // for several keys (CSDB/Graphics/Applicability/Manuals/Import); we
  // resolve the active project from the store + projects list. The
  // chord destination resolver (`chordTarget`) honours the same
  // path-type visibility rules the sidebar enforces: when the chord's
  // page is not applicable to the active project's pathType (e.g.
  // CSDB on an HTML project, Manuals on an S1000D project), we fall
  // back to a routable org-level page instead of routing into the
  // per-project URL (which is NOT wrapped by `ProjectScopedRedirect`
  // and would land on the wrong-pathType page).
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const projectsQ = useProjects('active');
  const activeProject =
    projectsQ.data?.find((p) => p.id === activeProjectId) ?? null;

  // Project-aware breadcrumb: the `:id` crumb shows the project name
  // (looked up here) and links to /projects/:id/dashboard. The map is
  // memoized so unrelated re-renders don't rebuild it.
  const projectsById = useMemo(() => {
    const m = new Map<string, { id: string; name: string }>();
    for (const p of projectsQ.data ?? []) m.set(p.id, { id: p.id, name: p.name });
    return m;
  }, [projectsQ.data]);

  const crumbs = buildCrumbs(location.pathname, projectsById);

  const [menuOpen, setMenuOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useClickOutside(menuRef, menuOpen, () => setMenuOpen(false));
  useEscapeKey(menuOpen, () => setMenuOpen(false));

  // WS-4b (C6) — global shortcuts. The hook short-circuits when focus
  // is inside the editor host or a form field so the editor's own
  // shortcuts (Ctrl+S, Ctrl+B, …) continue to work.
  const handlers = useMemo(
    () => ({
      onOpenSearch: () => {
        const el = document.querySelector<HTMLInputElement>(
          '[data-testid="global-search-input"]',
        );
        if (el) {
          el.focus();
          el.select?.();
        } else {
          navigate('/search');
        }
      },
      onToggleShortcutsModal: () => setShortcutsOpen((v) => !v),
      onOpenProfile: () => navigate('/profile'),
      onNavigateChord: (dest: ChordDestination) => {
        navigate(chordTarget(dest, activeProject));
      },
    }),
    [navigate, activeProject],
  );
  useGlobalShortcuts(handlers);
  const onCloseShortcuts = useCallback(() => setShortcutsOpen(false), []);

  const initials = (me?.name ?? '?')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  function menuStyle(): CSSProperties {
    const trigger = triggerRef.current;
    if (!trigger) return {};
    const rect = trigger.getBoundingClientRect();
    return { top: rect.bottom + 6, right: window.innerWidth - rect.right };
  }

  async function handleLogout(): Promise<void> {
    setMenuOpen(false);
    await logout.mutateAsync();
    navigate('/login', { replace: true });
  }

  return (
    <header className={styles.root} role="banner">
      <Link to="/projects" className={styles.logo} aria-label="TechAithor home">
        <span className={styles.logoMark} aria-hidden="true">
          <BrandMark className={styles.logoGlyph} />
        </span>
        <span>TechAithor</span>
      </Link>

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        {crumbs.length === 0 ? <span className={styles.crumb}>Home</span> : null}
        {crumbs.map((c, i) => (
          <span key={`${c.label}-${i}`} className={styles.crumb}>
            {i > 0 ? (
              <ChevronRight
                size={14}
                strokeWidth={1.5}
                aria-hidden="true"
                className={styles.crumbSep}
              />
            ) : null}
            {c.to ? <Link to={c.to}>{c.label}</Link> : c.label}
          </span>
        ))}
      </nav>

      <div className={styles.cluster}>
        {/* WS-4a (C2) — global search input + dropdown. Navigates via
            existing routes (DM editor for `dm` hits, etc.); no parallel
            mount paths. */}
        <GlobalSearchInput />
        {/* WS-4a (C1) — notifications bell + unread badge + slide-over.
            Backed by `/api/v1/notifications`. */}
        <NotificationBell />
        {/* WS-4b (C6) — keyboard shortcuts modal trigger. Cmd/Ctrl+/
            also toggles it via `useGlobalShortcuts`. */}
        <button
          type="button"
          className={styles.userTrigger}
          aria-label="Keyboard shortcuts"
          data-testid="open-shortcuts-modal"
          onClick={() => setShortcutsOpen(true)}
        >
          <Keyboard size={16} strokeWidth={1.5} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.userTrigger}
          aria-label="Help"
          onClick={() => navigate('/help')}
        >
          <HelpCircle size={16} strokeWidth={1.5} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.userTrigger}
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={toggleTheme}
        >
          {theme === 'dark' ? (
            <Sun size={16} strokeWidth={1.5} aria-hidden="true" />
          ) : (
            <Moon size={16} strokeWidth={1.5} aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          ref={triggerRef}
          className={styles.userTrigger}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className={styles.userAvatar} aria-hidden="true">
            {initials}
          </span>
        </button>
      </div>

      <ShortcutsModal open={shortcutsOpen} onClose={onCloseShortcuts} />

      {menuOpen
        ? createPortal(
            <div ref={menuRef} role="menu" className={styles.userMenu} style={menuStyle()}>
              <button
                type="button"
                role="menuitem"
                className={styles.userItem}
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/profile');
                }}
              >
                <User size={16} strokeWidth={1.5} aria-hidden="true" />
                Your Profile
              </button>
              {/* PROFILE-SLIMDOWN — the dedicated Security Settings
                  menu item has been removed; password, sessions, and
                  login history now live in tabs on /profile. The
                  /profile/security URL still redirects to /profile so
                  existing deep links continue to land correctly. */}
              <hr className={styles.userDivider} aria-hidden="true" />
              <button
                type="button"
                role="menuitem"
                className={`${styles.userItem} ${styles.userDanger}`}
                onClick={handleLogout}
              >
                <LogOut size={16} strokeWidth={1.5} aria-hidden="true" />
                Sign Out
              </button>
            </div>,
            document.body,
          )
        : null}
    </header>
  );
}
